import { ChangeDetectionStrategy, Component, computed, effect, inject, model, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import { CONFIRMER } from '@shared/components/dialogs/confirm/types';
import { PlatformChartListItemDto } from '@data/_generated-api/platform-api/models/platform-chart-list-item-dto';
import { PlatformChartRelationDto } from '@data/_generated-api/platform-api/models/platform-chart-relation-dto';
import { HdChartGroupService } from './hd-chart-group.service';
import { HdChartLibraryService } from './hd-chart-library.service';
import { buildChartDataSourceQuery } from './hd-chart-datasource-query';
import {
  HD_RELATION_TYPE_KEYS,
  hdRelationTypeIcon,
  hdRelationTypeLabel,
  isHdSystemRelationType,
} from './hd-chart-relation-metadata';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** null = gesloten. */
export interface HdChartRelationsTarget {
  chartId: string;
  chartName: string;
}

@Component({
  selector: 'hd-chart-relations-dialog',
  standalone: true,
  imports: [SharedModule, FormsModule, NgSelectModule],
  templateUrl: './hd-chart-relations-dialog.component.html',
  styleUrl: './hd-chart-relations-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartRelationsDialogComponent {
  private readonly groupService = inject(HdChartGroupService);
  private readonly library = inject(HdChartLibraryService);
  private readonly confirmer = inject(CONFIRMER);
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly target = model<HdChartRelationsTarget | null>(null);
  /** Emit de chart-id na elke geslaagde toevoeging/verwijdering (cache-invalidatie in de manager). */
  readonly changed = output<string>();

  protected readonly relations = signal<PlatformChartRelationDto[]>([]);

  /**
   * Alleen menselijke relaties: systeemlinks (cycleOf/compositeSource/momentOf
   * + inversen) horen in hun eigen secties (Cycles/Momenten) en zijn
   * zelfherstellend — hier tonen zou alleen een zinloze delete-knop opleveren.
   */
  protected readonly visibleRelations = computed(() =>
    this.relations().filter(relation => !isHdSystemRelationType(relation.relationType)),
  );
  /** Kandidaten voor de chart-picker: de volledige bibliotheek (alle pagina's). */
  private readonly candidates = signal<PlatformChartListItemDto[]>([]);
  protected readonly newChartId = signal<string | null>(null);
  protected readonly newType = signal<string | null>(null);
  protected readonly loading = signal(false);
  protected readonly error = signal('');

  constructor() {
    effect(() => {
      const target = this.target();
      if (!target) return;
      this.relations.set([]);
      this.newChartId.set(null);
      this.newType.set(null);
      this.error.set('');
      void this.loadForChart(target.chartId);
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chartManager.relations.title'),
      add: translate('humanDesign.chartManager.relations.add'),
      none: translate('humanDesign.chartManager.relations.none'),
      chartPlaceholder: translate('humanDesign.chartManager.relations.chartPlaceholder'),
      typePlaceholder: translate('humanDesign.chartManager.relations.typePlaceholder'),
      delete: translate('humanDesign.chartManager.relations.delete'),
      loading: translate('humanDesign.chartManager.detail.loading'),
    };
  });

  /** Kiesbare relatietypen met icoon (compositeSource bewust niet — gereserveerd). */
  protected readonly typeOptions = computed(() => {
    const translate = this.contentTranslate();
    return HD_RELATION_TYPE_KEYS.map(key => ({
      value: key as string,
      label: hdRelationTypeLabel(translate, key),
      icon: hdRelationTypeIcon(key),
    }));
  });

  /**
   * Chart-picker zonder de chart zelf. Al-gerelateerde charts blijven kiesbaar:
   * dezelfde persoon kan onder meerdere relatietypen gelinkt worden; een exact
   * duplicaat (incl. het teacher/student-spiegelbeeld) weigert de backend netjes.
   */
  protected readonly chartOptions = computed(() => {
    const target = this.target();
    if (!target) return [];
    return this.candidates()
      .filter(chart => chart.id && chart.id !== target.chartId)
      .map(chart => ({ value: chart.id as string, label: chart.name ?? '' }));
  });

  protected get canAdd(): boolean {
    return !!this.newChartId() && !!this.newType();
  }

  protected relationIcon(relationType: string | null | undefined): string {
    return hdRelationTypeIcon(relationType);
  }

  protected relationLabel(relationType: string | null | undefined): string {
    return hdRelationTypeLabel(this.contentTranslate(), relationType);
  }

  protected onOpenChange(open: boolean): void {
    if (!open) this.target.set(null);
  }

  protected async addRelation(): Promise<void> {
    const target = this.target();
    const toChartId = this.newChartId();
    const relationType = this.newType();
    if (!target || !toChartId || !relationType) return;

    this.error.set('');
    try {
      await this.groupService.createRelation(target.chartId, toChartId, relationType);
      this.relations.set(await this.groupService.listRelations(target.chartId));
      this.newChartId.set(null);
      this.changed.emit(target.chartId);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  protected async removeRelation(relation: PlatformChartRelationDto): Promise<void> {
    const target = this.target();
    if (!target || !relation.id) return;

    const message = this.transloco.translate<string>(
      'humanDesign.chartManager.relations.deleteConfirm',
      { name: relation.otherChartName ?? '' },
      this.contentLang.effectiveLang(),
    );
    const ok = await this.confirmer.confirm(message, { color: 'danger' });
    if (!ok) return;

    this.error.set('');
    try {
      await this.groupService.deleteRelation(relation.id);
      this.relations.set(await this.groupService.listRelations(target.chartId));
      this.changed.emit(target.chartId);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    }
  }

  private async loadForChart(chartId: string): Promise<void> {
    this.loading.set(true);
    try {
      const [relations, charts] = await Promise.all([
        this.groupService.listRelations(chartId),
        this.library.queryAllCharts(buildChartDataSourceQuery({ page: 1, pageSize: 100 })),
      ]);
      // Target kan intussen gewisseld/gesloten zijn; verouderd resultaat negeren.
      if (this.target()?.chartId !== chartId) return;
      this.relations.set(relations);
      this.candidates.set(charts);
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : `${e}`);
    } finally {
      this.loading.set(false);
    }
  }

  private contentTranslate(): (key: string) => string {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    return (key: string) => this.transloco.translate<string>(key, undefined, language);
  }
}
